import { useState, useEffect, useCallback, useRef } from 'react';
import { useTheme } from '@/contexts/ThemeContext';

export interface TimeUpdateModalProps {
  open: boolean;
  title?: string;
  message?: string;
  initialValue?: Date | null;
  confirmText?: string;
  cancelText?: string;
  scrollLock?: boolean;
  theme?: string;
  onConfirm: (dateTime: Date | null) => void;
  onCancel: () => void;
}

export function TimeUpdateModal({
  open,
  title = 'Update Time',
  message = 'Please select a time:',
  initialValue = null,
  confirmText = 'Update',
  cancelText = 'Cancel',
  scrollLock = true,
  theme: themeProp,
  onConfirm,
  onCancel,
}: TimeUpdateModalProps) {
  const { theme: globalTheme } = useTheme();
  const theme = themeProp ?? globalTheme;

  const [selectedDateTime, setSelectedDateTime] = useState<Date | null>(null);
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [isInTextMode, setIsInTextMode] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Scroll lock
  useEffect(() => {
    if (!scrollLock) return;
    if (open) {
      document.body.classList.add('kstc-scroll-lock');
    }
    return () => {
      document.body.classList.remove('kstc-scroll-lock');
    };
  }, [open, scrollLock]);

  // Initialize/reset state when modal opens/closes
  useEffect(() => {
    if (open) {
      setSelectedDateTime(initialValue ? new Date(initialValue) : new Date());
      setIsInTextMode(false);
      setIsPickerOpen(false);
    } else {
      setIsPickerOpen(false);
    }
  }, [open, initialValue]);

  // Escape key handler
  const handleEscape = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsPickerOpen(false);
        onCancel();
      }
    },
    [onCancel]
  );

  useEffect(() => {
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [handleEscape]);

  const handleConfirm = useCallback(() => {
    onConfirm(selectedDateTime);
  }, [selectedDateTime, onConfirm]);

  const handleCancel = useCallback(() => {
    setIsPickerOpen(false);
    onCancel();
  }, [onCancel]);

  const onDateTimeChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setSelectedDateTime(val ? new Date(val) : null);
  }, []);

  // Format date for datetime-local input
  const formatDateTimeLocal = (date: Date | null): string => {
    if (!date) return '';
    const pad = (n: number) => n.toString().padStart(2, '0');
    const year = date.getFullYear();
    const month = pad(date.getMonth() + 1);
    const day = pad(date.getDate());
    const hours = pad(date.getHours());
    const minutes = pad(date.getMinutes());
    const seconds = pad(date.getSeconds());
    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
  };

  if (!open) return null;

  return (
    <div data-kstc-modal className={`kstc-backdrop ${theme}`}>
      <div className="kstc-dialog kstc-dialog--time-update" role="dialog" aria-modal="true">
        <div className="kstc-header">
          <h2 className="kstc-title">{title}</h2>
          <button
            className="kstc-x"
            onClick={handleCancel}
            aria-label="Close"
            type="button"
          >
            &times;
          </button>
        </div>

        <div className="kstc-body">
          <p className="kstc-message">{message}</p>
          <div className="kstc-datetime-wrapper">
            <input
              type="datetime-local"
              className="kstc-input"
              step="1"
              value={formatDateTimeLocal(selectedDateTime)}
              onChange={onDateTimeChange}
            />
          </div>
        </div>

        <div className="kstc-actions">
          <button
            ref={buttonRef}
            className="kstc-btn kstc-btn--secondary"
            onClick={handleCancel}
            type="button"
          >
            {cancelText}
          </button>
          <button
            className="kstc-btn kstc-btn--success"
            onClick={handleConfirm}
            type="button"
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
}

(window as any).TimeUpdateModalReact = TimeUpdateModal;
